import { strapiClient } from "./strapiClient";
import { useQuery } from "@tanstack/vue-query";
import { type Brand } from "@/brandconfig";
import qs from "qs";

const client = strapiClient();

const THIRTY_MINUTES = 30 * 60 * 1000;

const collectionName = "brands";

const HOME_BLOCKS_SECTION = "homepageBlocks";

export const useGetBrandHomePageSections = (brand: Brand) => {
  const query = qs.stringify(
    {
      filters: {
        brand: {
          $eq: brand,
        },
      },
      populate: {
        [HOME_BLOCKS_SECTION]: {
          populate: {
            image: {
              fields: ["url", "formats"],
            },
          },
        },
      },
    },
    {
      encodeValuesOnly: true,
    }
  );

  return useQuery({
    queryKey: [HOME_BLOCKS_SECTION, brand],
    queryFn: async () => {
      const response = await client.get(`${collectionName}?${query}`);
      return response.data;
    },
    staleTime: THIRTY_MINUTES, // 30 minutes
    retry: false,
  });
};

type StrapiImageFormat = { url?: string };
type StrapiImage = {
  url?: string;
  formats?: {
    small?: StrapiImageFormat;
    thumbnail?: StrapiImageFormat;
  };
};

type HomePageBlock = {
  image?: StrapiImage;
  title?: string;
  url?: string;
  description?: string;
  tag?: string;
  type?: string;
};

export const normalizeHomePageBlocks = (strapiData: unknown) => {
  const blocksUnknown = (strapiData as { data?: Array<Record<string, unknown>> | undefined })?.data?.[0]?.[
    HOME_BLOCKS_SECTION
  ];
  const blocks = Array.isArray(blocksUnknown) ? (blocksUnknown as HomePageBlock[]) : [];

  const blockMap: { featured: Record<string, Partial<Record<string, string>>> } = {
    featured: {
      block1: {},
      block2: {},
      block3: {},
      block4: {},
      block5: {},
      block6: {},
    },
  };

  blocks.forEach((block: HomePageBlock, index: number) => {
    const blockKey = `block${index + 1}`;
    if (blockKey in blockMap.featured) {
      blockMap.featured[blockKey] = {
        // Prefer smaller Strapi formats when present
        photo: block.image?.formats?.medium?.url || block.image?.url || "",
        altText: block.title || "",
        to: block.url || "",
        title: block.title || "",
        description: block.description || "",
        tag: block.tag || "",
        events_link: block.url || "",
        linktext: block.type == "tournament" ? "View all tickets & events" : "View all available tickets",
        linktextmobile: block.type == "tournament" ? "Tickets & events" : "Available tickets",
      };
    }
  });

  return blockMap;
};
